import React, { useState, useEffect, useRef } from 'react';
import axios from '../../axios';
import config from "../../config";
import { Link, useHistory, useParams } from 'react-router-dom';
import BrokerAgreement from '../BrokerAgreement/BrokerAgreement';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInbox } from '@fortawesome/free-solid-svg-icons';
import './Inbox.css';
import { useLanguage } from '../../i18n/LanguageContext';

function Conversation({ conversationId, hideBackButton = false, onMessageSent }) {
  const [messages, setMessages] = useState([]);
  const [brokerId, setBrokerId] = useState(null);
  const [brokerName, setBrokerName] = useState('');
  const [brokerImage, setBrokerImage] = useState(null);
  const [newMessageContent, setNewMessageContent] = useState('');
  const [error, setError] = useState('');
  const backend_url = config.backend_url;
  const userType = localStorage.getItem('userType');
  const history = useHistory();
  const { conversationId: urlConversationId } = useParams();
  const { t, languageMeta } = useLanguage();
  const messagesEndRef = useRef(null);
  const [isAgreementAvailable, setIsAgreementAvailable] = useState(false);
  const [globalDisplayContactInfo, setGlobalDisplayContactInfo] = useState(false);
  const [displayContactInfo, setDisplayContactInfo] = useState(false);

  // Add a useEffect to log changes to displayContactInfo
  useEffect(() => {
    
  }, [displayContactInfo]);

  // Fetch broker details (id) for the link
  useEffect(() => {
    const fetchBrokerDetails = async () => {
      try {
        const response = await axios.get(
          `/api/conversations/${conversationId}/broker-description`,
          { withCredentials: true }
        );
        // Set the broker id from the response
        setBrokerId(response.data.id);
        
        // Fetch additional broker details including profile image
        if (response.data.id) {
          const brokerResponse = await axios.get(
            `${backend_url}/api/brokers/${response.data.id}`,
            { withCredentials: true }
          );
          
          // Set broker name and profile image
          const broker = brokerResponse.data;
          setBrokerName(`${broker.firstname} ${broker.lastname}`);
          setBrokerImage(broker.profile_image_url);
        }
      } catch (error) {
        console.error("Error fetching broker details", error);
      }
    };
    fetchBrokerDetails();
  }, [conversationId, backend_url]);

  // Enhanced useEffect to load conversation and contact info state on component mount
  useEffect(() => {
    // Initial data load
    const loadInitialData = async () => {
      try {
        // First load contact info settings
        await loadContactInfoSettings();
        // Then load messages
        await fetchMessages();
      } catch (error) {
        console.error("Error loading initial data:", error);
        setError(t('conversation.loadError'));
      }
    };
    
    loadInitialData();
    
    // Set up polling for messages as before
    // Only poll when the component is in focus/visible to save resources
    let intervalId;
    let isPageVisible = !document.hidden;
    
    // Check if page is visible
    const handleVisibilityChange = () => {
      isPageVisible = !document.hidden;
      
      // If coming back to visibility, refresh all data
      if (isPageVisible) {
        loadContactInfoSettings().then(() => fetchMessages());
      }
    };

    // Add a listener for the refresh-conversation event from BrokerAgreement
    const handleRefreshConversation = (event) => {
      // Check if this event is for our conversation
      if (event.detail && event.detail.conversationId === conversationId) {
        fetchMessages();
      }
    };
    
    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Add refresh conversation listener
    window.addEventListener('refresh-conversation', handleRefreshConversation);
    
    // Make fetchMessages available globally for direct calls
    window.refreshMessages = (convId) => {
      if (convId === conversationId) {
        fetchMessages();
      }
    };
    
    // Set up a polling interval with longer delay (30 seconds instead of 10)
    // and only poll when page is visible
    intervalId = setInterval(() => {
      if (isPageVisible) {
        fetchMessages();
      }
    }, 30000);
    
    // Clean up the interval when component unmounts
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('refresh-conversation', handleRefreshConversation);
      if (window.refreshMessages) {
        delete window.refreshMessages;
      }
    };
  }, [conversationId, backend_url]);

  // Enhanced loadContactInfoSettings with improved debugging
  const loadContactInfoSettings = async () => {
    try {
      if (userType === 'seller') {
        try {
          // Fetch seller's global setting - fix the URL by removing potential double slashes
          const userId = localStorage.getItem('userId');
          const sellerDataUrl = `${backend_url.replace(/\/+$/, '')}/api/sellers/${userId}/data`;
          
          const sellerDataResponse = await axios.get(sellerDataUrl, {
            withCredentials: true
          });
          
          if (sellerDataResponse.data && sellerDataResponse.data.display_contact_info !== undefined) {
            const globalValue = Boolean(sellerDataResponse.data.display_contact_info);
            setGlobalDisplayContactInfo(globalValue);
          }
        } catch (sellerDataError) {
          console.error("Error fetching global seller data:", sellerDataError);
          if (sellerDataError.response) {
            console.error("Response status:", sellerDataError.response.status);
            console.error("Response data:", sellerDataError.response.data);
          }
          // Continue with conversation-specific settings even if global settings fetch fails
        }
        
        try {
          // Fetch conversation-specific setting
          const conversationResponse = await axios.get(`${backend_url}/api/conversations/${conversationId}`, {
            withCredentials: true
          });
          
          if (conversationResponse.data && conversationResponse.data.display_contact_info !== undefined) {
            const newValue = Boolean(conversationResponse.data.display_contact_info);
            setDisplayContactInfo(newValue);
          }
        } catch (conversationError) {
          console.error("Error fetching conversation settings:", conversationError);
          if (conversationError.response) {
            console.error("Response status:", conversationError.response.status);
            console.error("Response data:", conversationError.response.data);
          }
        }
      }
    } catch (error) {
      console.error("Error loading contact info settings:", error);
      // Don't set general error here, as this is a background operation
    }
  };

  // Modified fetchMessages to not handle contact info anymore (separated concerns)
  const fetchMessages = async () => {
    try {
      // Fetch messages
      const messagesResponse = await axios.get(`${backend_url}/api/conversations/${conversationId}/messages`, {
        withCredentials: true,
      });
      setMessages(messagesResponse.data);
      setError('');
    } catch (error) {
      console.error('Error fetching messages:', error);
      setError(t('conversation.fetchMessagesError'));
    }
  };

  // Input sanitization function - modified to not trim during typing
  const sanitizeInput = (input, shouldTrim = false) => {
    if (!input) return '';
    // Remove null bytes and dangerous control characters, but preserve newlines (\n) and carriage returns (\r)
    const sanitized = input.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
    return shouldTrim ? sanitized.trim() : sanitized;
  };

  const handleSend = () => {
    // Sanitize the message content but don't trim to preserve intentional line breaks
    const sanitizedContent = sanitizeInput(newMessageContent, false);
    
    // Check if message is effectively empty (only whitespace/newlines)
    if (!sanitizedContent.trim()) {
      setError(t('conversation.emptyMessage'));
      return;
    }

    if (sanitizedContent.length > 5000) {
      setError(t('conversation.tooLong'));
      return;
    }

    // Clear any previous errors
    setError('');

    axios.post(`${backend_url}/api/conversations/${conversationId}/messages`, { content: sanitizedContent }, {
      withCredentials: true,
    })
      .then(() => {
        setNewMessageContent('');
        // re-fetch messages after sending a new one
        fetchMessages();
        // Notify parent component that message was sent to refresh unread count
        if (onMessageSent) {
          onMessageSent();
        }
      })
      .catch(error => {
        console.error('Error sending message:', error);
        if (error.response?.data?.message) {
          setError(error.response.data.message);
        } else {
          setError(t('conversation.sendError'));
        }
      });
  };

  // Handle message input changes with minimal sanitization during typing
  const handleMessageInputChange = (e) => {
    // Only remove control characters during typing, don't trim
    const sanitized = sanitizeInput(e.target.value, false);
    setNewMessageContent(sanitized);
    
    // Clear error if user starts typing and content is valid
    if (sanitized.trim() && sanitized.length <= 5000) {
      setError('');
    }
  };

  const getMessageContentWithLink = (content) => {
    // Check for markdown format with [Object link](url)
    const markdownRegex = /\[Object link\]\(([^)]+)\)/;
    const markdownMatch = content.match(markdownRegex);
    
    if (markdownMatch) {
      const link = markdownMatch[1];
      
      // Split the content to get text before and after the markdown link
      const parts = content.split(markdownRegex);
      let mainText = parts[0].trim();
      let textAfterLink = parts[2] ? parts[2].trim() : ''; // Get text after the link
      
      // Check if the message starts with "Annons"
      const annonsMatcher = /^Annons\s+([^,\n]+)/i; // Stop at comma or newline
      const annonsMatch = mainText.match(annonsMatcher);
      
      if (annonsMatch) {
        const streetName = annonsMatch[1].trim(); // Extract only the street name
        let restOfMessage = mainText.replace(annonsMatch[0], '').trim(); // Remove the entire "Annons [streetName]" part
        
        // Remove leading comma and whitespace if present
        if (restOfMessage.startsWith(',')) {
          restOfMessage = restOfMessage.substring(1).trim();
        }
        
        // Combine the rest of the message with text after the link
        let combinedRestOfMessage = '';
        if (restOfMessage && textAfterLink) {
          combinedRestOfMessage = `${restOfMessage}\n\n${textAfterLink}`;
        } else if (restOfMessage) {
          combinedRestOfMessage = restOfMessage;
        } else if (textAfterLink) {
          combinedRestOfMessage = textAfterLink;
        }
        
        return (
          <>
            <div>
              <a href={link} target="_blank" rel="noopener noreferrer">{t('conversation.objectLink', { street: streetName })}</a>
            </div>
            {combinedRestOfMessage && <div style={{ marginTop: '8px', whiteSpace: 'pre-line' }}>{combinedRestOfMessage}</div>}
          </>
        );
      } else {
        // If it doesn't start with "Annons", try to extract any text after the first line
        const lines = mainText.split('\n');
        const firstLine = lines[0].trim();
        const restOfMessage = lines.slice(1).join('\n').trim();
        
        // Combine the rest of the message with text after the link
        let combinedRestOfMessage = '';
        if (restOfMessage && textAfterLink) {
          combinedRestOfMessage = `${restOfMessage}\n\n${textAfterLink}`;
        } else if (restOfMessage) {
          combinedRestOfMessage = restOfMessage;
        } else if (textAfterLink) {
          combinedRestOfMessage = textAfterLink;
        }
        
        return (
          <>
            <div>
              <a href={link} target="_blank" rel="noopener noreferrer">{firstLine || t('conversation.viewObject')}</a>
            </div>
            {combinedRestOfMessage && <div style={{ marginTop: '8px', whiteSpace: 'pre-line' }}>{combinedRestOfMessage}</div>}
          </>
        );
      }
    }
    
    // Legacy checks for other link formats
    
    // Check for "Object link" format without markdown
    if (content.includes("Object link")) {
      // Parse the content to separate the message and the link
      const parts = content.split("Object link");
      const userMessage = parts[0].trim();
      const link = parts[1] ? parts[1].trim() : '';
      
      // Check if the message starts with "Annons"
      const annonsMatcher = /^Annons\s+(.+?)(?:\n|$)/i;
      const match = userMessage.match(annonsMatcher);
      
      if (match) {
        const streetName = match[1]; // Extract the street name
        let restOfMessage = userMessage.replace(annonsMatcher, '').trim();
        
        return (
          <>
            <div>
              <a href={link} target="_blank" rel="noopener noreferrer">{t('conversation.objectLink', { street: streetName })}</a>
            </div>
            {restOfMessage && <div style={{ marginTop: '8px', whiteSpace: 'pre-line' }}>{restOfMessage}</div>}
          </>
        );
      } else {
        // If message doesn't start with "Annons", split by lines
        const lines = userMessage.split('\n');
        const firstLine = lines[0].trim();
        const restOfMessage = lines.slice(1).join('\n').trim();
        
        return (
          <>
            <div>
              <a href={link} target="_blank" rel="noopener noreferrer">{firstLine || t('conversation.viewObject')}</a>
            </div>
            {restOfMessage && <div style={{ marginTop: '8px', whiteSpace: 'pre-line' }}>{restOfMessage}</div>}
          </>
        );
      }
    }
    
    // Generic Markdown link detection
    const generalMarkdownRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    if (generalMarkdownRegex.test(content)) {
      // Replace all markdown links with anchor tags
      const parts = [];
      let lastIndex = 0;
      let match;
      const regex = new RegExp(generalMarkdownRegex);
      
      while ((match = regex.exec(content)) !== null) {
        // Add text before the link with preserved line breaks
        if (match.index > lastIndex) {
          const textBefore = content.substring(lastIndex, match.index);
          parts.push(<span key={`text-${lastIndex}`} style={{ whiteSpace: 'pre-line' }}>{textBefore}</span>);
        }
        
        // Add the link
        const linkText = match[1];
        const url = match[2];
        parts.push(
          <a key={match.index} href={url} target="_blank" rel="noopener noreferrer">
            {linkText}
          </a>
        );
        
        lastIndex = regex.lastIndex;
      }
      
      // Add any remaining text with preserved line breaks
      if (lastIndex < content.length) {
        const remainingText = content.substring(lastIndex);
        parts.push(<span key={`text-${lastIndex}`} style={{ whiteSpace: 'pre-line' }}>{remainingText}</span>);
      }
      
      return <>{parts}</>;
    }
    
    // Regular URL detection
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    if (urlRegex.test(content)) {
      return (
        <div style={{ whiteSpace: 'pre-line' }}>
          {content.split(urlRegex).map((part, index, array) => {
            if (urlRegex.test(part)) {
              return <a key={index} href={part} target="_blank" rel="noopener noreferrer">{part}</a>;
            }
            return part;
          })}
        </div>
      );
    }
    
    // Just return the content if no links are found - preserve line breaks
    return <div style={{ whiteSpace: 'pre-line' }}>{content}</div>;
  };

  // Determine if a message is from the current user
  const isCurrentUserMessage = (message) => {
    return message.sender_type === userType;
  };

  // Enhanced toggleContactInfo function with improved state management
  const toggleContactInfo = async () => {
    try {
      // If already true, prevent toggling back
      if (displayContactInfo === true) {
        return;
      }
      
      setError(''); // Clear any previous errors
      
      const newValue = true; // Only allow toggling to true
      
      // Optimistic UI update
      setDisplayContactInfo(newValue);
      
      const response = await axios.put(
        `${backend_url}/api/conversations/${conversationId}/display-contact-info`, 
        { display_contact_info: newValue },
        { withCredentials: true }
      );
      
      if (response.status === 200) {
        // Send a notification message to the broker
        const notificationMessage = t('conversation.contactNotification');
        
        await axios.post(
          `${backend_url}/api/conversations/${conversationId}/messages`,
          { content: notificationMessage },
          { withCredentials: true }
        );
        
        // Refresh messages to include the new notification
        fetchMessages();
        
        // Add a brief delay to ensure database update has processed
        setTimeout(() => {
          // Reload contact info settings to verify the update in the database
          loadContactInfoSettings();
        }, 1000);
      } else {
        console.error('Unexpected response toggling contact info:', response);
        // Revert optimistic update if API call fails
        setDisplayContactInfo(false);
        setError(t('objectForm.validation.unexpected'));
      }
    } catch (error) {
      console.error('Error toggling contact info visibility:', error);
      // Revert optimistic update if API call fails
      setDisplayContactInfo(false);
      setError(
        error.response?.data?.error || 
        t('objectForm.validation.network')
      );
    }
  };

  // Add contact info permission UI for sellers
  const renderContactInfoToggle = () => {
    if (userType === 'seller' && !globalDisplayContactInfo) {
      return (
        <div className="checkbox-group" style={{
          display: 'flex',
          alignItems: 'center',
          margin: '0.8rem 0 1.5rem',
          padding: '10px 15px',
          backgroundColor: displayContactInfo ? '#e8f5e9' : '#f8f9fa',
          borderRadius: '8px',
          borderLeft: '4px solid #4CAF50',
          boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)',
          transition: 'all 0.2s ease'
        }}>
          <input
            type="checkbox"
            id="contact-info-toggle"
            checked={displayContactInfo === true}
            onChange={toggleContactInfo}
            disabled={displayContactInfo === true}
            style={{
              cursor: displayContactInfo ? 'not-allowed' : 'pointer'
            }}
          />
          <label htmlFor="contact-info-toggle" style={{
            margin: '0 0 0 12px',
            fontWeight: '500',
            color: '#424242',
            fontSize: '0.95rem',
            cursor: displayContactInfo ? 'default' : 'pointer',
            flex: '1'
          }}>
            {t('conversation.shareContact')}
            <span style={{
              display: 'block',
              fontWeight: '400',
              color: '#757575',
              fontSize: '0.8rem',
              marginTop: '3px'
            }}>
              {displayContactInfo 
                ? t('conversation.contactShared')
                : t('conversation.contactNotShared')}
            </span>
          </label>
          {error && <div className="error-message">{error}</div>}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="conversation-container" data-conversation-id={conversationId} style={{ 
      margin: '0 auto', 
      padding: '20px',
      backgroundColor: 'var(--duck-blue-10)',
      borderRadius: '8px',
      maxWidth: '1200px',
      width: '100%'
    }}>
      {/* Back to inbox button - moved above the header */}
      {!hideBackButton && (
        <button 
          className="back-button"
          onClick={() => history.push('/inbox')}
          style={{
            padding: '10px 20px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: 'var(--blue-4)',
            color: 'white',
            fontWeight: 'bold',
            cursor: 'pointer',
            marginBottom: '15px',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <span style={{ marginRight: '5px' }}>←</span> {t('conversation.back')}
        </button>
      )}
      
      {/* Only show Inkorg header when not used inside Inbox component */}
      {!hideBackButton && (
        <h2><FontAwesomeIcon icon={faInbox} className="inbox-icon" /> {t('conversation.inbox')}</h2>
      )}
      
      {/* Broker profile link placed in the Conversation component */}
      {brokerId && (
        <div style={{ 
          marginBottom: '1.5rem',
          padding: '10px',
          borderRadius: '6px',
          backgroundColor: 'var(--duck-blue-9)',
          display: 'inline-block'
        }}>
          <Link 
            to={{
              pathname: `/broker/${brokerId}`,
              state: { fromConversation: conversationId, preserveHeader: true }
            }}
            style={{
              color: 'var(--blue-3)',
              textDecoration: 'none',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <span style={{ marginRight: '5px' }}>👤</span> {t('conversation.goToBrokerProfile')}
          </Link>
        </div>
      )}
      
      {/* Render contact info toggle */}
      {renderContactInfoToggle()}
      
      <BrokerAgreement conversationId={conversationId} />
      
      {error && (
        <p style={{ 
          color: 'var(--error-2)', 
          backgroundColor: 'var(--error-3)', 
          padding: '10px', 
          borderRadius: '6px',
          marginBottom: '15px',
          border: '1px solid var(--error-1)'
        }}>
          {error}
        </p>
      )}
      
      <div className="conversation-messages" style={{ marginBottom: '20px' }}>
        {[...messages].reverse().map(message => (
          <div 
            key={message.id} 
            className={`message ${isCurrentUserMessage(message) ? 'sender' : 'receiver'}`}
            style={{
              backgroundColor: isCurrentUserMessage(message) ? 'var(--duck-blue-6)' : 'var(--white)',
              color: 'var(--grey-1)',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '12px',
              maxWidth: '75%',
              alignSelf: isCurrentUserMessage(message) ? 'flex-end' : 'flex-start',
              border: '1px solid',
              borderColor: isCurrentUserMessage(message) ? 'var(--duck-blue-5)' : 'var(--grey-8)',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
              position: 'relative',
              marginLeft: isCurrentUserMessage(message) ? 'auto' : '0',
              wordBreak: 'break-word'
            }}
          >
            {/* Broker profile image and name - show for all broker messages */}
            {message.sender_type === 'broker' && (
              <div className="broker-info">
                <div className="broker-avatar">
                  {brokerImage ? (
                    <img 
                      src={brokerImage} 
                      alt={brokerName} 
                    />
                  ) : (
                    <span style={{ fontSize: '14px', color: 'var(--grey-5)' }}>
                      {brokerName ? brokerName.charAt(0) : t('conversation.broker').charAt(0)}
                    </span>
                  )}
                </div>
                <div className="broker-name">
                  {brokerName || t('conversation.broker')}
                </div>
              </div>
            )}
            
            {/* Seller label for seller messages */}
            {message.sender_type === 'seller' && (
              <div className="broker-info">
                <div className="broker-avatar" style={{ backgroundColor: 'var(--duck-blue-7)' }}>
                  <span style={{ fontSize: '14px', color: 'var(--blue-3)' }}>S</span>
                </div>
                <div className="broker-name">{t('conversation.seller')}</div>
              </div>
            )}
            
            {!isCurrentUserMessage(message) && !message.is_read && (
              <div className="unread-indicator" style={{
                position: 'absolute',
                top: '5px',
                right: '5px',
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: 'var(--blue-4)',
              }}></div>
            )}
            <div style={{ marginBottom: '4px' }}>
              {getMessageContentWithLink(message.content)}
            </div>
            <div className="message-timestamp">
              {(() => {
                // Parse the timestamp from the server (UTC format)
                const date = new Date(message.timestamp);
                
                // Add timezone offset to match the expected display time
                // We're adding 2 hours (7200000 ms) for proper timezone conversion
                const adjustedDate = new Date(date.getTime() + 7200000);
                
                return adjustedDate.toLocaleString(languageMeta?.locale || 'en-US', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                });
              })()}
            </div>
          </div>
        ))}
      </div>
      
      <div className="message-input-container" style={{
        display: 'flex',
        flexDirection: 'column',
        marginTop: '15px',
        borderTop: '1px solid var(--grey-9)',
        paddingTop: '15px'
      }}>
        <textarea
          value={newMessageContent}
          onChange={handleMessageInputChange}
          placeholder={t('conversation.placeholder')}
          style={{
            width: '100%',
            padding: '10px 15px',
            borderRadius: '8px',
            border: '1px solid var(--grey-8)',
            minHeight: '80px',
            resize: 'vertical',
            fontSize: '1rem',
            marginBottom: '5px'
          }}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
            // Shift+Enter will create a new line (default behavior)
          }}
        />
        
        {/* Character count display */}
        <div style={{
          fontSize: '0.85rem',
          color: newMessageContent.length > 5000 ? 'var(--error-2)' : 'var(--grey-5)',
          textAlign: 'right',
          marginBottom: '10px'
        }}>
          {t('conversation.chars', { count: newMessageContent.length })}
        </div>
        
        <button 
          onClick={handleSend}
          style={{
            alignSelf: 'flex-start',
            padding: '10px 20px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: 'var(--blue-4)',
            color: 'white',
            fontWeight: 'bold',
            cursor: 'pointer',
            width: '100px'
          }}
        >
          {t('conversation.send')}
        </button>
      </div>
      
      {/* Render broker agreement buttons */}
      {userType === 'seller' && (
        <div className="broker-agreement-actions">
          {/* ... existing broker agreement buttons ... */}
        </div>
      )}
    </div>
  );
}

export default Conversation;
